export default function CoursesSection() {
  const courses = [
    {
      icon: "ri-book-2-line",
      title: "Simple Grammar Made Easy",
      description: "No confusing rules or boring textbooks. Learn grammar the natural way with clear explanations and practical examples you'll actually use in real conversations.",
      features: ["Step-by-step explanations", "Real-life examples", "Common mistakes avoided", "Practice exercises"],
      color: "blue",
      image: "https://static.readdy.ai/image/fe93cdafcb89f7e9cc6f2d2b3ea438ac/1409282c57a362613debef13a4152deb.jpeg"
    },
    {
      icon: "ri-chat-3-line",
      title: "Useful Vocabulary That Matters",
      description: "Skip the useless words! Learn the most important vocabulary that native speakers use every day. Build your word power with words that actually matter.",
      features: ["Daily-use words only", "Memory techniques", "Context-based learning", "Practical applications"],
      color: "green",
      image: "https://public.readdy.ai/ai/img_res/2644fb49b71231a1bbdd65d2773c1b7e.jpg"
    },
    {
      icon: "ri-mic-line",
      title: "Perfect Pronunciation Tips",
      description: "Sound like a native speaker! Master the sounds that matter most. Clear pronunciation guides that help you speak confidently from day one.",
      features: ["Sound practice drills", "Common pronunciation mistakes", "Accent reduction tips", "Speaking confidence"],
      color: "purple",
      image: "https://readdy.ai/api/search-image?query=Person%20practicing%20English%20pronunciation%20with%20headphones%20and%20microphone%2C%20modern%20home%20study%20setup%2C%20speech%20training%20materials%2C%20confident%20speaking%20practice%20environment&width=400&height=300&seq=pronunciation-course&orientation=landscape"
    },
    {
      icon: "ri-star-line",
      title: "Real American Slang",
      description: "Understand what Americans really say! Learn the slang, idioms, and expressions that make you sound natural and help you fit in with native speakers.",
      features: ["Current slang terms", "Cultural context", "When to use them", "Sound more natural"],
      color: "orange",
      image: "https://readdy.ai/api/search-image?query=American%20flag%20and%20casual%20conversation%20bubbles%20with%20slang%20expressions%2C%20modern%20cultural%20learning%20materials%2C%20friendly%20and%20approachable%20American%20English%20study%20setup&width=400&height=300&seq=slang-course&orientation=landscape"
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: "bg-blue-50 text-blue-600 border-blue-200",
      green: "bg-green-50 text-green-600 border-green-200",
      purple: "bg-purple-50 text-purple-600 border-purple-200",
      orange: "bg-orange-50 text-orange-600 border-orange-200"
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <section id="courses" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Everything You Need to Speak English Confidently
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our proven method focuses on the four essential skills that make the biggest difference. 
            No fluff, no boring theory - just practical English that works in the real world.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {courses.map((course, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={course.image} 
                  alt={course.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className={`absolute top-4 left-4 w-12 h-12 rounded-full flex items-center justify-center ${getColorClasses(course.color)}`}>
                  <i className={`${course.icon} text-xl`}></i>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{course.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{course.description}</p>
                
                <ul className="space-y-2">
                  {course.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                      <i className="ri-check-line text-green-600 mr-2 flex-shrink-0"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Why Our Method Works So Well
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                <i className="ri-focus-3-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="font-semibold mb-2">Focus on What Matters</h4>
              <p className="text-sm text-gray-600">We teach only the English you'll actually use, not textbook theory</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <i className="ri-speed-line text-2xl text-green-600"></i>
              </div>
              <h4 className="font-semibold mb-2">Learn Faster</h4>
              <p className="text-sm text-gray-600">Our practical approach gets you speaking confidently in weeks</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-3">
                <i className="ri-home-heart-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="font-semibold mb-2">Study at Home</h4>
              <p className="text-sm text-gray-600">Learn on your schedule, at your pace, from anywhere</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
